import { Fragment, type FC } from 'react';
import { formatScore } from 'FormatScore';
import { type StreamForStreamDashboard, type LessonForStreamDashboard } from 'Lessons';
import { type GetLessonAccess, type StreamSummaryLink } from '../StreamDashboard.types';
import {
    getChapterLessons,
    getCompletedLessonsInChapter,
    isLessonComingSoonInStream,
    isLessonComplete,
    isLessonStarted,
    getLessonLaunchTextKey,
    getLessonBestScore,
    shouldShowAssessmentArc,
} from '../selectors';
import { useChapterExpansion } from '../hooks/useChapterExpansion';
import { ChapterBox } from './ChapterBox';
import { LessonRow } from './LessonRow';

/*
    The chapter accordion. It holds the per-lesson decisions, so ChapterBox and LessonRow stay presentational.
    A no_user lesson is clickable: the click opens the sign-up modal.
*/

type Props = {
    stream: StreamForStreamDashboard;
    isSignedIn: boolean;

    // A phone gets the locked reason as a modal, so the row must not also anchor a tooltip.
    isMobile: boolean;
    summariesForLesson: (lesson: LessonForStreamDashboard) => StreamSummaryLink[];
    summariesDisabled: boolean;

    // Comes from the page so the whole page shares one access cache. See useLessonAccess.
    getLessonAccess: GetLessonAccess;
    onLaunchLesson: (lesson: LessonForStreamDashboard) => void;
    onLockedLesson: (reasonMessage: string) => void;
    onSummaryClick: (summaryId: string) => void;
};

export const ChapterList: FC<Props> = ({
    stream,
    isSignedIn,
    isMobile,
    summariesForLesson,
    summariesDisabled,
    getLessonAccess,
    onLaunchLesson,
    onLockedLesson,
    onSummaryClick,
}) => {
    const { isChapterExpanded, toggleChapter } = useChapterExpansion(stream);

    return (
        <>
            {stream.chapters.map((chapter, chapterIndex) => {
                const lessons = getChapterLessons(stream, chapter);
                const isPending = !!chapter.pending;

                return (
                    <ChapterBox
                        className="mb-7.5"
                        key={chapterIndex}
                        chapterNumber={chapterIndex + 1}
                        title={chapter.title}
                        completedLessonCount={getCompletedLessonsInChapter(stream, chapter).length}
                        totalLessonCount={lessons.length}
                        isExpanded={isChapterExpanded(chapterIndex)}
                        isPending={isPending}
                        onToggle={() => toggleChapter(chapterIndex)}
                    >
                        {lessons.map((lesson, lessonIndex) => {
                            const access = getLessonAccess(lesson);
                            const isClickable = access.canLaunch || access.reason === 'no_user';

                            return (
                                <Fragment key={lesson.id}>
                                    <LessonRow
                                        lesson={lesson}
                                        lessonNumber={lessonIndex + 1}
                                        isComingSoon={isLessonComingSoonInStream(stream, lesson)}
                                        isSignedIn={isSignedIn}
                                        isInProgress={isLessonStarted(lesson)}
                                        isComplete={isLessonComplete(lesson)}
                                        canLaunch={access.canLaunch}
                                        isClickable={isClickable}
                                        showAssessmentArc={shouldShowAssessmentArc(stream, lesson)}
                                        showMobileScore={shouldShowAssessmentArc(stream, lesson, {
                                            hideInProgress: true,
                                        })}
                                        formattedBestScore={formatScore(getLessonBestScore(lesson) ?? 0)}
                                        launchTextKey={getLaunchTextKey(stream, lesson, access)}
                                        summaries={summariesForLesson(lesson)}
                                        summariesDisabled={summariesDisabled}
                                        onLaunch={() => onLaunchLesson(lesson)}
                                        onRowClick={() => {
                                            // A visitor is not locked out, they need the sign-up upsell, which
                                            // tryToLaunchLesson opens off the no_user reason.
                                            if (isClickable) onLaunchLesson(lesson);
                                            else onLockedLesson(access.reasonMessage ?? '');
                                        }}
                                        lockedTooltipMessage={
                                            !isMobile && !isClickable ? (access.reasonMessage ?? null) : null
                                        }
                                        onSummaryClick={onSummaryClick}
                                    />
                                    {/* 1px slate-grey-200, the full width of the box. Every lesson gets
                                        one, the last included, so the list reads as closed before the
                                        chapter's bottom bar. */}
                                    <div className="h-px bg-slate-grey-200" />
                                </Fragment>
                            );
                        })}
                    </ChapterBox>
                );
            })}
        </>
    );
};

/*
    Mirrors ContentAccessHelper#launchText, which wraps the plain review/resume/start text: a locked
    item reads LOCKED, and a finished one reads COMPLETED. A completed exam is special-cased to
    COMPLETED even when it could be launched again.
*/
function getLaunchTextKey(
    stream: StreamForStreamDashboard,
    lesson: LessonForStreamDashboard,
    access: { canLaunch: boolean },
) {
    const isCompletedExam = stream.exam && !!stream.lessonStreamsProgress?.complete;

    if (access.canLaunch && !isCompletedExam) return `launch_${getLessonLaunchTextKey(lesson)}`;
    if (isLessonComplete(lesson)) return 'launch_completed';
    return 'launch_locked';
}
